import React from 'react';
import { Plus, Check, X } from 'lucide-react';
interface ArmaTuPlatoPanelProps {
  lines: string[];
  setLines: React.Dispatch<React.SetStateAction<string[]>>;
  onAddToCart: (filledLines: string[]) => void;
}
/**
 * Arma tu plato — panel exclusivo para Rancho San Francisco.
 *
 * Muestra una lista de inputs de texto libre donde el mesero escribe
 * cada línea del plato (ej. "Arroz", "4 chicharrones", "Ensalada",
 * "Papas"). Al presionar "Agregar al pedido" todas las líneas no vacías
 * se agregan al carrito como ítems personalizados (sin menu_item_id).
 *
 * El padre se encarga de transformar las líneas en WaiterCartItem y
 * resetear el panel. Aquí solo manejamos UI + estado de los inputs.
 */
export function ArmaTuPlatoPanel({
  lines,
  setLines,
  onAddToCart
}: ArmaTuPlatoPanelProps) {
  const updateLine = (index: number, value: string) => {
    setLines(prev => prev.map((l, i) => i === index ? value : l));
  };
  const addRow = () => {
    setLines(prev => [...prev, '']);
  };
  const removeRow = (index: number) => {
    setLines(prev => {
      const next = prev.filter((_, i) => i !== index);
      // Mantener al menos 1 fila visible
      return next.length === 0 ? [''] : next;
    });
  };
  const handleAdd = () => {
    const filled = lines.map(l => l.trim()).filter(l => l.length > 0);
    if (filled.length === 0) return;
    onAddToCart(filled);
  };
  const filledCount = lines.filter(l => l.trim().length > 0).length;
  return <div className="max-w-3xl mx-auto" data-id="element-2111">
      {/* Header */}
      <div className="mb-6" data-id="element-2112">
        <h2 className="text-2xl font-black text-gray-900 mb-1" data-id="element-2113">
          Arma tu plato
        </h2>
        <p className="text-sm text-gray-500" data-id="element-2114">
          Escribe cada línea del plato. Ejemplo: <em data-id="element-2115">Arroz</em>,{' '}
          <em data-id="element-2116">4 chicharrones</em>, <em data-id="element-2117">Ensalada</em>, <em data-id="element-2118">Papas</em>. Cada línea
          se envía a cocina como una instrucción.
        </p>
      </div>

      {/* Lines */}
      <div className="space-y-2 mb-4" data-id="element-2119">
        {lines.map((line, index) => <div key={index} className="flex items-center gap-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-2" data-id="element-2120">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-50 text-amber-700 font-bold rounded-xl shrink-0" data-id="element-2121">
              {index + 1}
            </div>
            <input type="text" value={line} onChange={e => updateLine(index, e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            // Si es la última fila y tiene contenido, agrega otra
            if (index === lines.length - 1 && line.trim().length > 0) {
              addRow();
            }
          }
        }} placeholder={`Línea ${index + 1} (ej. ${['Arroz', 'Chicharrones', 'Ensalada', 'Papas', 'Frijoles', 'Plátano'][index % 6]})`} className="flex-1 h-12 px-3 bg-transparent border-0 text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none" autoFocus={index === 0 && line.length === 0} data-id="element-2122" />
            <button type="button" onClick={() => removeRow(index)} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors shrink-0" aria-label={`Eliminar línea ${index + 1}`} data-id="element-2123">
              <X className="w-5 h-5" data-id="element-2124" />
            </button>
          </div>)}
      </div>

      {/* Add row */}
      <button type="button" onClick={addRow} className="w-full h-12 mb-6 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 text-gray-600 font-bold rounded-2xl hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50 transition-colors" data-id="element-2125">
        <Plus className="w-5 h-5" data-id="element-2126" />
        Agregar otra línea
      </button>

      {/* Submit */}
      <button type="button" onClick={handleAdd} disabled={filledCount === 0} className="w-full h-14 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-lg font-bold rounded-2xl transition-colors" data-id="element-2127">
        <Check className="w-6 h-6" data-id="element-2128" />
        Agregar al pedido
        {filledCount > 0 && <span className="ml-1 bg-white/20 px-2 py-0.5 rounded-full text-sm" data-id="element-2129">
            {filledCount} {filledCount === 1 ? 'línea' : 'líneas'}
          </span>}
      </button>
    </div>;
}